import { useMemo, useState } from "react";
import { normalizeUserStatus } from "./Helpers";
import { t, setLang } from "../../../i18n";

function splitMultilineValues(value) {
  return String(value || "")
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseParameterLines(text) {
  return String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [rawKey, ...rest] = line.split(":");
      const key = String(rawKey || "").trim();
      const value = rest.join(":").trim();
      return key && value ? { key, value } : null;
    })
    .filter(Boolean);
}

function parseSpecOptionLines(text) {
  return String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [rawKey, ...rest] = line.split(":");
      const key = String(rawKey || "").trim();
      const values = rest
        .join(":")
        .split(/[|,，/]/)
        .map((item) => item.trim())
        .filter(Boolean);
      if (!key || !values.length) {
        return null;
      }

      return { key, values };
    })
    .filter(Boolean);
}

function buildSkuCombinations(specGroups, fallbackPrice, fallbackMoq) {
  if (!Array.isArray(specGroups) || !specGroups.length) {
    return [];
  }

  const combinations = specGroups.reduce((acc, group) => {
    const current = Array.isArray(group?.values) ? group.values : [];
    if (!current.length) {
      return acc;
    }

    if (!acc.length) {
      return current.map((value) => ({ [group.key]: value }));
    }

    const next = [];
    acc.forEach((item) => {
      current.forEach((value) => {
        next.push({ ...item, [group.key]: value });
      });
    });
    return next;
  }, []);

  return combinations.slice(0, 60).map((item, index) => ({
    id: index + 1,
    attrs: item,
    price: Number(fallbackPrice || 0) || 0,
    moq: Number(fallbackMoq || 0) || 0,
    leadTime: ""
  }));
}

export function PositionMaintenanceTable({
  texts,
  filteredAdminUsers,
  userDraftById,
  toUserDraft,
  assignableRoleOptions,
  positionSelectOptions,
  userSavingId,
  formatUserTime,
  onEditUser,
  onChangeUserDraft,
  onSaveUser,
  onOpenPasswordModal
}) {
  function getRoleLabel(roleValue) {
    const matched = assignableRoleOptions.find((item) => item.value === roleValue);
    return matched?.label || roleValue || "-";
  }

  // If texts is not provided, fallback to t('admin.xxx')
  const i18nTexts = texts || new Proxy({}, {
    get: (_, key) => t(`admin.${key}`)
  });
  return (
    <section className="controlhub-card controlhub-wide-card user-management-panel">
      <div className="table-wrap">
        <table className="controlhub-table user-management-table">
          <colgroup>
            <col className="user-management-col user-management-col-serial" />
            <col className="user-management-col user-management-col-userid" />
            <col className="user-management-col user-management-col-username" />
            <col className="user-management-col user-management-col-nickname" />
            <col className="user-management-col user-management-col-email" />
            <col className="user-management-col user-management-col-phone" />
            <col className="user-management-col user-management-col-role" />
            <col className="user-management-col user-management-col-status" />
            <col className="user-management-col user-management-col-position" />
            <col className="user-management-col user-management-col-registered" />
            <col className="user-management-col user-management-col-last-active" />
            <col className="user-management-col user-management-col-actions" />
          </colgroup>
          <thead>
            <tr>
              <th className="user-management-cell-serial">{i18nTexts.serialNumber}</th>
              <th className="user-management-cell-userid">{i18nTexts.userId}</th>
              <th className="user-management-cell-username">{i18nTexts.username}</th>
              <th className="user-management-cell-nickname">{i18nTexts.nickname}</th>
              <th className="user-management-cell-email">{i18nTexts.email}</th>
              <th className="user-management-cell-phone">{i18nTexts.phone}</th>
              <th className="user-management-cell-role">{i18nTexts.userRole}</th>
              <th className="user-management-cell-status">{i18nTexts.accountStatus}</th>
              <th className="user-management-cell-position">{i18nTexts.position}</th>
              <th className="user-management-cell-registered">{i18nTexts.registeredAt}</th>
              <th className="user-management-cell-last-active">{i18nTexts.lastActiveAt}</th>
              <th className="user-management-cell-actions">{i18nTexts.actions}</th>
            </tr>
          </thead>
          <tbody>
            {filteredAdminUsers.map((item, index) => (
              <tr key={`position-${item.id}`}>
                <td className="user-management-cell-serial">{index + 1}</td>
                <td className="user-management-cell-userid" title={String(item.id || "-")}>{item.id}</td>
                <td className="user-management-cell-username" title={item.username || "-"}>{item.username || "-"}</td>
                <td className="user-management-cell-nickname">
                  <input
                    type="text"
                    title={(userDraftById[item.id] && userDraftById[item.id].name) || item.name || ""}
                    value={(userDraftById[item.id] && userDraftById[item.id].name) || item.name || ""}
                    onChange={(event) => onChangeUserDraft(item.id, "name", event.target.value, item, toUserDraft)}
                  />
                </td>
                <td className="user-management-cell-email">
                  <input
                    type="text"
                    title={(userDraftById[item.id] && userDraftById[item.id].email) || item.email || ""}
                    value={(userDraftById[item.id] && userDraftById[item.id].email) || item.email || ""}
                    onChange={(event) => onChangeUserDraft(item.id, "email", event.target.value, item, toUserDraft)}
                  />
                </td>
                <td className="user-management-cell-phone">
                  <input
                    type="text"
                    title={(userDraftById[item.id] && userDraftById[item.id].phone) || item.phone || ""}
                    value={(userDraftById[item.id] && userDraftById[item.id].phone) || item.phone || ""}
                    onChange={(event) => onChangeUserDraft(item.id, "phone", event.target.value, item, toUserDraft)}
                  />
                </td>
                <td className="user-management-cell-role">
                  <select
                    title={getRoleLabel((userDraftById[item.id] && userDraftById[item.id].role) || item.role || "customer")}
                    value={(userDraftById[item.id] && userDraftById[item.id].role) || item.role || "customer"}
                    onChange={(event) => onChangeUserDraft(item.id, "role", event.target.value, item, toUserDraft)}
                  >
                    {assignableRoleOptions.map((roleOption) => (
                      <option key={`role-${item.id}-${roleOption.value}`} value={roleOption.value}>
                        {roleOption.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="user-management-cell-status">
                  <div className="controlhub-status-toggle" role="group" aria-label={i18nTexts.accountStatus}>
                    {[
                      { value: "active", label: i18nTexts.statusActive },
                      { value: "disactive", label: i18nTexts.statusDisactive }
                    ].map((statusOption) => {
                      const currentStatus = normalizeUserStatus(
                        (userDraftById[item.id] && userDraftById[item.id].status) || item.status || "active"
                      );
                      const isActive = currentStatus === statusOption.value;

                      return (
                        <button
                          key={`status-${item.id}-${statusOption.value}`}
                          type="button"
                          className={`controlhub-status-toggle-button${isActive ? " is-active" : ""}`}
                          aria-pressed={isActive}
                          title={statusOption.label}
                          onClick={() => onChangeUserDraft(item.id, "status", statusOption.value, item, toUserDraft)}
                        >
                          {statusOption.label}
                        </button>
                      );
                    })}
                  </div>
                </td>
                <td className="user-management-cell-position">
                  <select
                    title={(userDraftById[item.id] && userDraftById[item.id].jobTitle) || item.jobTitle || ""}
                    value={(userDraftById[item.id] && userDraftById[item.id].jobTitle) || item.jobTitle || ""}
                    onChange={(event) => onChangeUserDraft(item.id, "jobTitle", event.target.value, item, toUserDraft)}
                  >
                    <option value="">-</option>
                    {positionSelectOptions.map((positionName) => (
                      <option key={`position-option-${item.id}-${positionName}`} value={positionName}>
                        {positionName}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="user-management-cell-registered" title={formatUserTime(item.registeredAt)}>{formatUserTime(item.registeredAt)}</td>
                <td className="user-management-cell-last-active" title={formatUserTime(item.lastActiveAt)}>{formatUserTime(item.lastActiveAt)}</td>
                <td className="user-management-cell-actions">
                  <div className="row-actions user-management-actions">
                    <button
                      type="button"
                      className="controlhub-button"
                      title={userSavingId === item.id ? `${i18nTexts.save}...` : i18nTexts.save}
                      onClick={() => onSaveUser(item)}
                      disabled={userSavingId === item.id}
                    >
                      {userSavingId === item.id ? `${i18nTexts.save}...` : i18nTexts.save}
                    </button>
                    <button
                      type="button"
                      className="controlhub-button ghost"
                      title={i18nTexts.changePasswordAction}
                      onClick={() => onOpenPasswordModal(item)}
                    >
                      {i18nTexts.changePasswordAction}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function ProductsTable({
  texts,
  filteredAdminProducts,
  analyticsByProductId,
  analyticsDailyByProductId,
  analyticsLoading,
  analyticsRange,
  onChangeAnalyticsRange,
  onRefreshAnalytics,
  onExportAnalyticsDaily,
  analyticsExporting,
  onStartEdit,
  onDeleteProduct,
  onToggleRecommended,
  onOpenCreate,
  locale
}) {
  const isZh = locale === "zh";
  const [hoveredSparkPoint, setHoveredSparkPoint] = useState(null);

  function isProductRecommended(value) {
    return value === true || value === 1 || String(value || "").trim().toLowerCase() === "true";
  }

  function formatProductName(item) {
    if (typeof item?.name === "string") {
      return item.name;
    }
    return item?.name?.zh || item?.name?.en || "-";
  }

  function formatProductPrice(item) {
    const value = Number(item?.priceUSD || 0);
    if (!Number.isFinite(value) || value <= 0) {
      return isZh ? "待补充" : "Pending";
    }
    return `US $${value.toFixed(2)} / Piece`;
  }

  function formatProductDate(item) {
    const dateSource = item?.updatedAt || item?.createdAt || Date.now();
    const parsed = new Date(dateSource);
    if (Number.isNaN(parsed.getTime())) {
      return "-";
    }
    return parsed.toISOString().slice(0, 10);
  }

  function formatDuration(secondsValue) {
    const seconds = Number(secondsValue || 0);
    if (!Number.isFinite(seconds) || seconds <= 0) {
      return isZh ? "0秒" : "0s";
    }
    if (seconds < 60) {
      return isZh ? `${Math.round(seconds)}秒` : `${Math.round(seconds)}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainSeconds = Math.round(seconds % 60);
    return isZh ? `${minutes}分${remainSeconds}秒` : `${minutes}m ${remainSeconds}s`;
  }

  function formatPercent(value) {
    const normalized = Number(value || 0);
    if (!Number.isFinite(normalized) || normalized <= 0) {
      return "0%";
    }
    return `${(normalized * 100).toFixed(1)}%`;
  }

  function buildSparklinePolyline(values) {
    const points = Array.isArray(values) ? values : [];
    if (points.length <= 1) {
      return "";
    }

    const width = 84;
    const height = 24;
    const padding = 2;
    const maxValue = Math.max(1, ...points);
    const stepX = points.length > 1 ? (width - padding * 2) / (points.length - 1) : 0;

    return points
      .map((value, index) => {
        const x = padding + index * stepX;
        const ratio = Number(value || 0) / maxValue;
        const y = height - padding - ratio * (height - padding * 2);
        return `${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(" ");
  }

  function buildSparklinePoints(values) {
    const points = Array.isArray(values) ? values : [];
    const width = 84;
    const height = 24;
    const padding = 2;
    const maxValue = Math.max(1, ...points.map((item) => Number(item?.value || 0)));
    const stepX = points.length > 1 ? (width - padding * 2) / (points.length - 1) : 0;

    return points.map((item, index) => {
      const numericValue = Number(item?.value || 0);
      const ratio = numericValue / maxValue;
      const x = padding + index * stepX;
      const y = height - padding - ratio * (height - padding * 2);
      return {
        x,
        y,
        date: String(item?.date || "").trim(),
        value: Math.max(0, Math.round(numericValue))
      };
    });
  }

  function formatTrendPointTooltip(point) {
    const date = String(point?.date || "").trim() || (isZh ? "未知日期" : "Unknown date");
    const value = Number(point?.value || 0);
    return isZh ? `${date} 浏览量: ${value}` : `${date} Views: ${value}`;
  }

  function getProductQuality(item) {
    let score = 70;
    if (String(item?.imageUrl || "").trim()) {
      score += 10;
    }
    if (String(item?.description?.en || item?.description || "").trim()) {
      score += 8;
    }
    if (Array.isArray(item?.parameters) && item.parameters.length >= 3) {
      score += 7;
    }
    if (Array.isArray(item?.mainImages) && item.mainImages.length >= 4) {
      score += 5;
    }
    return Math.min(99, score);
  }

  function getProductStatus(item) {
    const score = getProductQuality(item);
    if (score >= 90) {
      return isZh ? "已上架" : "Published";
    }
    if (score >= 80) {
      return isZh ? "待完善" : "Needs polish";
    }
    return isZh ? "待发布" : "Draft";
  }

  function getQualityTone(score) {
    if (score >= 90) {
      return "positive";
    }
    if (score >= 80) {
      return "accent";
    }
    return "warning";
  }

  return (
    <section className="controlhub-card controlhub-full-width controlhub-product-management-panel">
      <div className="controlhub-product-management-toolbar row-actions admin-commerce-product-toolbar">
        <div className="admin-commerce-product-toolbar-group">
          <label className="admin-commerce-product-date-filter">
            <span>{isZh ? "开始" : "Start"}</span>
            <input
              type="date"
              value={analyticsRange?.startDate || ""}
              onChange={(event) => onChangeAnalyticsRange?.("startDate", event.target.value)}
            />
          </label>
          <label className="admin-commerce-product-date-filter">
            <span>{isZh ? "结束" : "End"}</span>
            <input
              type="date"
              value={analyticsRange?.endDate || ""}
              onChange={(event) => onChangeAnalyticsRange?.("endDate", event.target.value)}
            />
          </label>
        </div>

        <div className="admin-commerce-product-toolbar-actions">
          <button type="button" className="controlhub-button ghost" onClick={() => onRefreshAnalytics?.()} disabled={analyticsLoading}>
            {analyticsLoading ? (isZh ? "刷新中..." : "Refreshing...") : (isZh ? "刷新统计" : "Refresh Analytics")}
          </button>
          <button type="button" className="controlhub-button" onClick={() => onExportAnalyticsDaily?.()} disabled={analyticsExporting}>
            {analyticsExporting ? (isZh ? "导出中..." : "Exporting...") : (isZh ? "导出日报 CSV" : "Export Daily CSV")}
          </button>
          {onOpenCreate ? (
            <button type="button" className="controlhub-button ghost" onClick={onOpenCreate}>
              {texts.createProduct}
            </button>
          ) : null}
        </div>
      </div>

      <div className="controlhub-product-management-header-row">
        <span>{isZh ? "营销品名" : "Product"}</span>
        <span>{isZh ? "商品报价" : "Offer"}</span>
        <span>{isZh ? "负责人 / 更新时间" : "Owner / Updated"}</span>
        <span>{isZh ? "质量 / 状态 / 趋势" : "Quality / Status / Trend"}</span>
        <span>{isZh ? "操作" : "Actions"}</span>
      </div>

      <div className="controlhub-product-management-list">
        {filteredAdminProducts.map((item) => {
          const qualityScore = getProductQuality(item);
          const statusText = getProductStatus(item);
          const productName = formatProductName(item);
          const typeTierText = isZh ? "商机品 / 潜力优品" : "Opportunity / Potential";
          const recommended = isProductRecommended(item?.isRecommended);
          const isReadOnly = Boolean(item?.readOnly);
          const analytics = analyticsByProductId?.[String(item.id)] || null;
          const dailyTrend = Array.isArray(analyticsDailyByProductId?.[String(item.id)])
            ? analyticsDailyByProductId[String(item.id)]
            : [];
          const viewCount = Number(analytics?.viewCount || 0);
          const avgStaySeconds = Number(analytics?.averageDwellTimeSeconds || 0);
          const bounceRate = Number(analytics?.bounceRate || 0);
          const recentViewCount = Number(analytics?.recentViewCount || 0);
          const trendPoints = dailyTrend.map((point) => ({
            date: String(point?.date || "").trim(),
            value: Number(point?.viewCount || 0)
          }));
          const trendValues = trendPoints.map((point) => point.value);
          const sparkline = buildSparklinePolyline(trendValues);
          const sparklinePoints = buildSparklinePoints(trendPoints);
          const lastPointValue = trendValues.length ? trendValues[trendValues.length - 1] : 0;
          const activePoint = hoveredSparkPoint && hoveredSparkPoint.productId === item.id
            ? sparklinePoints[hoveredSparkPoint.index] || null
            : null;
          const qualityTone = getQualityTone(qualityScore);

          return (
            <article className="controlhub-product-management-row" key={item.id}>
              <div className="controlhub-product-name-cell">
                <div className="controlhub-product-thumb-wrap">
                  {item.imageUrl ? <img src={item.imageUrl} alt={productName} loading="lazy" /> : <span>IMG</span>}
                </div>
                <div className="controlhub-product-name-meta">
                  <strong>
                    <a
                      className="controlhub-product-title-link"
                      href={`/products/${encodeURIComponent(item.id)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {productName}
                    </a>
                  </strong>
                  <div className="admin-commerce-product-meta-line">
                    <span className="admin-commerce-product-meta-item">
                      {isZh ? `分组 ${item.category || "Others"}` : `Group ${item.category || "Others"}`}
                    </span>
                    <span className="admin-commerce-product-meta-item">ID {item.id}</span>
                    <span className="admin-commerce-product-meta-item">SKU {item.sku || "-"}</span>
                  </div>
                </div>
              </div>

              <div className="controlhub-product-cell-stack admin-commerce-product-offer-cell">
                <strong>{formatProductPrice(item)}</strong>
                <span>{typeTierText}</span>
              </div>

              <div className="controlhub-product-cell-stack admin-commerce-product-owner-cell">
                <strong>{item.owner || "Loya Swift"}</strong>
                <span>{formatProductDate(item)}</span>
                <small>{isZh ? "最后同步时间" : "Last sync date"}</small>
              </div>

              <div className="admin-commerce-product-signal-panel">
                <div className={`admin-commerce-product-signal-card tone-${qualityTone}`}>
                  <span>{isZh ? "质量分" : "Quality"}</span>
                  <strong>{qualityScore}{isZh ? "分" : " pts"}</strong>
                  <small>{isZh ? "智能诊断" : "AI Diagnosis"}</small>
                </div>

                <div className="admin-commerce-product-signal-card tone-slate">
                  <span>{isZh ? "状态" : "Status"}</span>
                  <strong>{statusText}</strong>
                  <small>{isZh ? "发布准备度" : "Readiness"}</small>
                </div>

                <div className="admin-commerce-product-signal-card tone-trend controlhub-product-trend-cell">
                  <div className="admin-commerce-product-signal-chart-head">
                    <span>{isZh ? "趋势" : "Trend"}</span>
                    <strong>{recentViewCount}</strong>
                  </div>
                  <svg width="84" height="24" viewBox="0 0 84 24" role="img" aria-label={isZh ? "按天浏览趋势" : "Daily views trend"}>
                    <rect x="0" y="0" width="84" height="24" rx="4" fill="rgba(0,0,0,0.04)" />
                    {sparkline ? (
                      <>
                        <polyline
                          fill="none"
                          stroke="#177ddc"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          points={sparkline}
                        />
                        {activePoint ? (
                          <line
                            x1={activePoint.x}
                            y1="2"
                            x2={activePoint.x}
                            y2="22"
                            stroke="#4a6fa1"
                            strokeWidth="1"
                            strokeDasharray="2 2"
                            opacity="0.7"
                          />
                        ) : null}
                        {sparklinePoints.map((point, index) => (
                          <circle
                            key={`${item.id}-spark-${index}`}
                            cx={point.x}
                            cy={point.y}
                            r={activePoint && activePoint.date === point.date && activePoint.value === point.value ? "3" : "2"}
                            fill="#177ddc"
                            opacity={activePoint && activePoint.date !== point.date ? "0.6" : "0.95"}
                            style={{ cursor: "pointer" }}
                            tabIndex={0}
                            onMouseEnter={() => setHoveredSparkPoint({ productId: item.id, index })}
                            onMouseLeave={() => setHoveredSparkPoint((current) => (
                              current && current.productId === item.id && current.index === index ? null : current
                            ))}
                            onFocus={() => setHoveredSparkPoint({ productId: item.id, index })}
                            onBlur={() => setHoveredSparkPoint((current) => (
                              current && current.productId === item.id && current.index === index ? null : current
                            ))}
                          >
                            <title>{formatTrendPointTooltip(point)}</title>
                          </circle>
                        ))}
                      </>
                    ) : (
                      <line x1="2" y1="18" x2="82" y2="18" stroke="#9fb3c8" strokeWidth="1.2" strokeDasharray="4 3" />
                    )}
                  </svg>
                  {activePoint ? (
                    <div className="controlhub-product-trend-tooltip">
                      {formatTrendPointTooltip(activePoint)}
                    </div>
                  ) : null}
                  <small>
                    {isZh ? `最新 ${lastPointValue} · 停留 ${formatDuration(avgStaySeconds)}` : `Latest ${lastPointValue} · Stay ${formatDuration(avgStaySeconds)}`}
                  </small>
                  <small>{isZh ? `跳出率 ${formatPercent(bounceRate)}` : `Bounce ${formatPercent(bounceRate)}`}</small>
                </div>
              </div>

              <div className="controlhub-product-actions-col">
                <button
                  type="button"
                  className={`controlhub-button ghost${recommended ? " is-active" : ""}`}
                  onClick={() => onToggleRecommended?.(item.id, !recommended)}
                  disabled={isReadOnly}
                >
                  {recommended
                    ? (isZh ? "已推荐" : "Recommended")
                    : (isZh ? "推荐" : "Recommend")}
                </button>
                <button
                  type="button"
                  className="controlhub-button admin-commerce-product-action-primary"
                  onClick={() => onStartEdit(item)}
                  disabled={isReadOnly}
                >
                  {texts.edit}
                </button>
                <button
                  type="button"
                  className="controlhub-button ghost admin-commerce-product-action-danger"
                  onClick={() => onDeleteProduct(item.id)}
                  disabled={isReadOnly}
                >
                  {texts.remove}
                </button>
              </div>
            </article>
          );
        })}

        {!filteredAdminProducts.length ? (
          <div className="controlhub-product-management-empty">
            <p>{isZh ? "暂无商品，请从左侧“商品发布”进入创建。" : "No products yet. Go to Product Publish in the left menu."}</p>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export function CreateProductForm({
  texts,
  newProduct,
  setNewProduct,
  onSubmit,
  productCategories,
  locale,
  onBack,
  onUploadAssets,
  onDeleteAssets,
  editorTitle,
  editorEyebrow,
  primaryActionLabel
}) {
  const [uploadNotice, setUploadNotice] = useState("");
  const [dragTarget, setDragTarget] = useState("");

  const isZh = locale === "zh";
  const mainImages = useMemo(() => splitMultilineValues(newProduct.mainImagesText), [newProduct.mainImagesText]);
  const detailImages = useMemo(() => splitMultilineValues(newProduct.detailImagesText), [newProduct.detailImagesText]);
  const parameterRows = useMemo(() => parseParameterLines(newProduct.parametersText), [newProduct.parametersText]);
  const detailNarrativeBlocks = useMemo(() => splitMultilineValues(newProduct.detailContentText), [newProduct.detailContentText]);
  const coreReady = Boolean(
    String(newProduct.category || "").trim()
    && String(newProduct.sku || "").trim()
    && String(newProduct.model || "").trim()
    && String(newProduct.title || "").trim()
  );
  const pricingReady = Boolean(Number(newProduct.priceUSD) > 0 && String(newProduct.priceUnitCode || "").trim());
  const mediaReady = Boolean(String(newProduct.imageUrl || "").trim() && mainImages.length === 6);
  const contentReady = Boolean(String(newProduct.description || "").trim() && parameterRows.length > 0 && String(newProduct.detailContentText || "").trim());
  const editorSections = useMemo(() => ([
    {
      key: "core",
      anchor: "ops-editor-core",
      label: isZh ? "核心资料" : "Core Fields",
      done: coreReady
    },
    {
      key: "pricing",
      anchor: "ops-editor-pricing",
      label: isZh ? "价格与展示" : "Pricing",
      done: pricingReady
    },
    {
      key: "media",
      anchor: "ops-editor-media",
      label: isZh ? "媒体素材" : "Media",
      done: mediaReady
    },
    {
      key: "content",
      anchor: "ops-editor-content",
      label: isZh ? "参数与详情" : "Specs and Detail",
      done: contentReady
    }
  ]), [contentReady, coreReady, isZh, mediaReady, pricingReady]);
  const completedSections = editorSections.filter((item) => item.done).length;
  const databaseFieldNotes = isZh
    ? [
      "保留：SKU、型号、分类、标题、副标题、品牌、价格、计价单位、销量、热卖标记、主图/详情图、视频、参数、详情正文。",
      "移除：物流备注、服务承诺、售后策略、阶梯价预览、SKU 组合预览、详情编排建议等非 products 主表录入项。",
      "主图仍按现有接口要求保留 6 张，首图会自动作为封面图。"
    ]
    : [
      "Keep: SKU, model, category, title, subtitle, brand, price, price unit, sales count, bestseller flag, media, parameters, and detail body.",
      "Removed: logistics notes, service commitments, after-sales policy, ladder pricing previews, SKU preview tables, and editorial coaching blocks.",
      "The current API still requires 6 main images, and the first one is used as the cover image."
    ];
  const priceUnitOptions = ["piece", "set", "pair", "box", "carton", "kg"];

  function updateField(field, value) {
    setNewProduct((prev) => ({ ...prev, [field]: value }));
  }

  function getManagedDraftUrls(value) {
    return splitMultilineValues(value).filter((item) => String(item || "").includes("/uploads/"));
  }

  function scrollToSection(anchor) {
    const target = document.getElementById(anchor);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  async function processImageFiles(field, files, limit) {
    const pickedFiles = Array.from(files || []).filter(Boolean);
    if (!pickedFiles.length) {
      return;
    }

    const previousUrls = getManagedDraftUrls(newProduct[field]);
    const nextUrls = typeof onUploadAssets === "function"
      ? await onUploadAssets(pickedFiles.slice(0, limit), { kind: field === "mainImagesText" ? "main-image" : "detail-image" })
      : [];
    if (previousUrls.length && typeof onDeleteAssets === "function") {
      await onDeleteAssets(previousUrls);
    }
    updateField(field, nextUrls.join("\n"));
    if (field === "mainImagesText" && nextUrls[0]) {
      updateField("imageUrl", nextUrls[0]);
    }
    setUploadNotice(isZh ? "素材已上传并写入草稿。" : "Assets were uploaded and added to the draft.");
  }

  async function handleImageUpload(field, event, limit) {
    const files = Array.from(event.target.files || []);
    if (!files.length) {
      return;
    }

    try {
      await processImageFiles(field, files, limit);
    } catch (error) {
      setUploadNotice(error?.message || (isZh ? "素材读取失败" : "Failed to read assets"));
    } finally {
      event.target.value = "";
    }
  }

  function handleUploadDragEnter(targetKey) {
    setDragTarget(targetKey);
  }

  function handleUploadDragLeave(event, targetKey) {
    const nextTarget = event.relatedTarget;
    if (event.currentTarget.contains(nextTarget)) {
      return;
    }
    setDragTarget((current) => (current === targetKey ? "" : current));
  }

  async function handleUploadDrop(event, field, limit, targetKey) {
    event.preventDefault();
    setDragTarget("");
    const droppedFiles = Array.from(event.dataTransfer?.files || []).filter((file) => String(file?.type || "").startsWith("image/"));
    if (!droppedFiles.length) {
      setUploadNotice(isZh ? "请拖入图片文件。" : "Please drop image files only.");
      return;
    }

    try {
      await processImageFiles(field, droppedFiles, limit);
    } catch (error) {
      setUploadNotice(error?.message || (isZh ? "素材读取失败" : "Failed to read assets"));
    }
  }

  async function handleVideoUpload(event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const previousUrls = String(newProduct.detailVideoUrl || "").includes("/uploads/") ? [newProduct.detailVideoUrl] : [];
      const uploadedUrls = typeof onUploadAssets === "function"
        ? await onUploadAssets([file], { kind: "video" })
        : [];
      if (previousUrls.length && typeof onDeleteAssets === "function") {
        await onDeleteAssets(previousUrls);
      }
      updateField("detailVideoUrl", uploadedUrls[0] || "");
      setUploadNotice(isZh ? "详情视频已上传。" : "The detail video was uploaded.");
    } catch (error) {
      setUploadNotice(error?.message || (isZh ? "视频读取失败" : "Failed to read the video"));
    } finally {
      event.target.value = "";
    }
  }

  return (
    <article className="controlhub-card controlhub-full-width controlhub-ops-editor-shell">
      <div className="controlhub-ops-editor-layout">
        <aside className="controlhub-ops-editor-sidebar">
          <div className="controlhub-ops-editor-sidebar-scroll">
            <div className="controlhub-ops-editor-score-card">
              <div>
                <strong>{completedSections}/{editorSections.length}</strong>
                <span>{isZh ? "字段区块完成度" : "Section Completion"}</span>
              </div>
              <p>{isZh ? "当前面板已收敛为 products 实际录入字段。" : "The editor is now limited to persisted product fields."}</p>
            </div>

            <div className="controlhub-ops-editor-nav-panel">
              <div className="controlhub-ops-editor-side-list">
                {editorSections.map((section) => (
                  <button
                    key={section.key}
                    type="button"
                    className="controlhub-ops-editor-side-item"
                    onClick={() => scrollToSection(section.anchor)}
                  >
                    <span>{section.label}</span>
                    <strong>{section.done ? (isZh ? "OK" : "OK") : (isZh ? "待" : "-")}</strong>
                  </button>
                ))}
              </div>
            </div>

            <div className="controlhub-ops-editor-tip-card">
              <h5>{isZh ? "数据库字段范围" : "Database Scope"}</h5>
              <ul className="controlhub-ops-editor-db-list">
                {databaseFieldNotes.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </aside>

        <div className="controlhub-ops-editor-main">
          <div className="controlhub-ops-editor-toolbar">
            <div>
              <p className="controlhub-ops-editor-eyebrow">{editorEyebrow || (isZh ? "运营专员商品详情上传" : "Operations Product Detail Upload")}</p>
              <h4>{editorTitle || (isZh ? "产品编辑" : "Product Editor")}</h4>
              <p className="controlhub-merchandising-caption">
                {isZh
                  ? "按数据库实际商品字段录入，保留核心资料、价格、媒体素材、规格参数和详情正文。"
                  : "Edit only the product fields that map to the persisted product record: identity, pricing, media, specs, and detail body."}
              </p>
              <div className="controlhub-ops-editor-toolbar-pills">
                <span>{newProduct.category || (isZh ? "未选分类" : "No category")}</span>
                <span>{`${mainImages.length}/6 ${isZh ? "主图" : "main images"}`}</span>
                <span>{`${newProduct.priceUnitCode || "piece"} / ${newProduct.salesCount || "0"}`}</span>
              </div>
            </div>
            <div className="row-actions">
              {onBack ? (
                <button type="button" className="controlhub-button ghost" onClick={() => { void onBack(); }}>
                  {isZh ? "返回商品列表" : "Back to Products"}
                </button>
              ) : null}
              <button type="submit" form="ops-product-editor-form" className="controlhub-button ghost">
                {isZh ? "发布" : "Publish"}
              </button>
              <button type="submit" form="ops-product-editor-form" className="controlhub-button">
                {primaryActionLabel || (isZh ? "保存" : texts.create)}
              </button>
            </div>
          </div>
          <div className="controlhub-ops-editor-main-body">
            <form id="ops-product-editor-form" className="controlhub-form controlhub-ops-editor-form" onSubmit={onSubmit}>
              <section className="controlhub-ops-editor-overview" aria-label={isZh ? "商品编辑概览" : "Product editor overview"}>
                <article className="controlhub-ops-editor-overview-card tone-primary">
                  <span>{isZh ? "当前标题" : "Current Title"}</span>
                  <strong>{newProduct.title || (isZh ? "待填写商品标题" : "Product title pending")}</strong>
                  <p>{newProduct.subtitle || (isZh ? "副标题可补充一句场景或卖点。" : "Use the subtitle for a concise scenario or value cue.")}</p>
                </article>
                <article className="controlhub-ops-editor-overview-card tone-warm">
                  <span>{isZh ? "价格与单位" : "Price and Unit"}</span>
                  <strong>{newProduct.priceUSD ? `$${newProduct.priceUSD}` : "$0.00"}</strong>
                  <p>{isZh ? `单位 ${newProduct.priceUnitCode || "piece"}` : `Unit ${newProduct.priceUnitCode || "piece"}`}</p>
                </article>
                <article className="controlhub-ops-editor-overview-card tone-cool">
                  <span>{isZh ? "媒体完成度" : "Media Coverage"}</span>
                  <strong>{`${mainImages.length}/6`}</strong>
                  <p>{isZh ? `${detailImages.length} 张详情图，${newProduct.detailVideoUrl ? "含视频" : "无视频"}` : `${detailImages.length} detail images, ${newProduct.detailVideoUrl ? "video linked" : "no video"}`}</p>
                </article>
                <article className="controlhub-ops-editor-overview-card tone-neutral">
                  <span>{isZh ? "内容密度" : "Content Density"}</span>
                  <strong>{parameterRows.length}</strong>
                  <p>{isZh ? `${detailNarrativeBlocks.length} 个详情段落` : `${detailNarrativeBlocks.length} detail blocks`}</p>
                </article>
              </section>

              <section id="ops-editor-core" className="controlhub-ops-editor-section">
                <div className="controlhub-ops-editor-section-head">
                  <div className="controlhub-ops-editor-section-lead">
                    <span className="controlhub-ops-editor-section-index">01</span>
                    <div>
                      <h5>{isZh ? "核心资料" : "Core Product Fields"}</h5>
                      <span>{isZh ? "围绕 products 实际标识与展示字段录入。" : "Fill the persisted identity and listing fields."}</span>
                    </div>
                  </div>
                  <span className="controlhub-ops-editor-section-tag">{isZh ? "基础档案" : "Identity"}</span>
                </div>
                <div className="controlhub-form-grid three-col">
                  <label>
                    <span>{texts.category}</span>
                    <select
                      value={newProduct.category}
                      onChange={(event) => updateField("category", event.target.value)}
                      required
                    >
                      {productCategories.map((category) => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>{texts.sku}</span>
                    <input value={newProduct.sku} onChange={(event) => updateField("sku", event.target.value)} required />
                  </label>
                  <label>
                    <span>{texts.model}</span>
                    <input value={newProduct.model} onChange={(event) => updateField("model", event.target.value)} required />
                  </label>
                </div>
                <div className="controlhub-form-grid three-col">
                  <label>
                    <span>{isZh ? "品牌" : "Brand"}</span>
                    <input
                      value={newProduct.brandName || ""}
                      onChange={(event) => updateField("brandName", event.target.value)}
                      placeholder={isZh ? "例如 LOYA" : "e.g. LOYA"}
                    />
                  </label>
                  <label>
                    <span>{isZh ? "计价单位" : "Price Unit"}</span>
                    <select
                      value={newProduct.priceUnitCode || "piece"}
                      onChange={(event) => updateField("priceUnitCode", event.target.value)}
                    >
                      {priceUnitOptions.map((unit) => (
                        <option key={unit} value={unit}>{unit}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>{isZh ? "销量" : "Sales Count"}</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={newProduct.salesCount ?? "0"}
                      onChange={(event) => updateField("salesCount", event.target.value)}
                    />
                  </label>
                </div>
                <div className="controlhub-form-grid two-col">
                  <label>
                    <span>{texts.titleField}</span>
                    <input
                      value={newProduct.title}
                      onChange={(event) => updateField("title", event.target.value)}
                      required
                    />
                  </label>
                  <label>
                    <span>{isZh ? "副标题" : "Subtitle"}</span>
                    <input
                      value={newProduct.subtitle || ""}
                      onChange={(event) => updateField("subtitle", event.target.value)}
                      placeholder={isZh ? "一句补充卖点或适用场景" : "A short supporting value statement"}
                    />
                  </label>
                </div>
                <label>
                  <span>{texts.description}</span>
                  <textarea
                    rows={5}
                    value={newProduct.description}
                    onChange={(event) => updateField("description", event.target.value)}
                    placeholder={isZh ? "这里写商品摘要，对应数据库里的简要描述。" : "Use this for the short product summary stored with the product record."}
                    required
                  />
                </label>
              </section>

              <section id="ops-editor-pricing" className="controlhub-ops-editor-section">
                <div className="controlhub-ops-editor-section-head">
                  <div className="controlhub-ops-editor-section-lead">
                    <span className="controlhub-ops-editor-section-index">02</span>
                    <div>
                      <h5>{isZh ? "价格与展示" : "Pricing and Merchandising Flags"}</h5>
                      <span>{isZh ? "保留数据库里的价格、销量和热卖展示控制。" : "Keep only the stored price and listing display fields."}</span>
                    </div>
                  </div>
                  <span className="controlhub-ops-editor-section-tag">{isZh ? "商业展示" : "Pricing"}</span>
                </div>
                <div className="controlhub-ops-editor-kpi-grid">
                  <article>
                    <span>{texts.price}</span>
                    <strong>{newProduct.priceUSD || "0.00"}</strong>
                  </article>
                  <article>
                    <span>{isZh ? "计价单位" : "Price Unit"}</span>
                    <strong>{newProduct.priceUnitCode || "piece"}</strong>
                  </article>
                  <article>
                    <span>{isZh ? "销量" : "Sales"}</span>
                    <strong>{newProduct.salesCount || "0"}</strong>
                  </article>
                </div>
                <div className="controlhub-form-grid three-col">
                  <label>
                    <span>{texts.price}</span>
                    <input type="number" step="0.01" value={newProduct.priceUSD} onChange={(event) => updateField("priceUSD", event.target.value)} required />
                  </label>
                  <label>
                    <span>{isZh ? "热卖手动置顶" : "Pin As Best Seller"}</span>
                    <div className="controlhub-ops-editor-switch-row">
                      <input
                        type="checkbox"
                        checked={newProduct.isBestSelling === true}
                        onChange={(event) => updateField("isBestSelling", event.target.checked)}
                      />
                      <small>
                        {isZh
                          ? "开启后该商品会在 Best-Selling 区域优先展示。"
                          : "When enabled, this product is prioritized in the Best-Selling section."}
                      </small>
                    </div>
                  </label>
                  <div className="controlhub-ops-editor-summary-card">
                    <strong>{isZh ? "已对齐字段" : "Aligned Fields"}</strong>
                    <p>{isZh ? "价格、单位、销量与热卖标记都直接对应商品记录。" : "Price, unit, sales, and bestseller state now map directly to the stored product record."}</p>
                  </div>
                </div>
              </section>

              <section id="ops-editor-media" className="controlhub-ops-editor-section">
                <div className="controlhub-ops-editor-section-head">
                  <div className="controlhub-ops-editor-section-lead">
                    <span className="controlhub-ops-editor-section-index">03</span>
                    <div>
                      <h5>{isZh ? "媒体素材" : "Media Assets"}</h5>
                      <span>{isZh ? "只保留封面图、主图、详情图和详情视频。" : "Keep only the cover, gallery, detail images, and detail video."}</span>
                    </div>
                  </div>
                  <span className="controlhub-ops-editor-section-tag">{isZh ? "视觉素材" : "Media"}</span>
                </div>
                <div className="controlhub-ops-editor-basic-grid">
                  <div className="controlhub-ops-editor-stack">
                    <div className="controlhub-form-grid two-col">
                      <label>
                        <span>{texts.imageUrl}</span>
                        <input
                          value={newProduct.imageUrl}
                          onChange={(event) => updateField("imageUrl", event.target.value)}
                          placeholder={isZh ? "主图地址会随上传自动回填" : "Primary image URL auto-fills after upload"}
                          required
                        />
                      </label>
                      <label>
                        <span>{texts.detailVideo}</span>
                        <input
                          value={newProduct.detailVideoUrl}
                          onChange={(event) => updateField("detailVideoUrl", event.target.value)}
                          placeholder={isZh ? "可粘贴详情视频地址或直接上传" : "Paste a detail video URL or upload a file"}
                        />
                      </label>
                    </div>
                    <div className="controlhub-ops-editor-media-actions">
                      <label className="controlhub-button ghost controlhub-ops-editor-upload-button">
                        {isZh ? "上传详情视频" : "Upload Detail Video"}
                        <input type="file" accept="video/*" onChange={handleVideoUpload} />
                      </label>
                      <div className="controlhub-ops-editor-inline-note">
                        {isZh ? "主图保持 6 张，详情视频用于补充展示，不再承载额外服务说明。" : "Keep exactly 6 main images. The video is optional and only supports the product presentation."}
                      </div>
                    </div>
                  </div>

                  <div className="controlhub-ops-editor-summary-card">
                    <strong>{isZh ? "媒体入库规则" : "Media Persistence Rules"}</strong>
                    <ul className="controlhub-ops-editor-db-list">
                      <li>{isZh ? "`imageUrl` 使用封面图。" : "`imageUrl` is used as the cover image."}</li>
                      <li>{isZh ? "`mainImages` 按现有接口固定要求 6 张。" : "`mainImages` still requires exactly 6 URLs."}</li>
                      <li>{isZh ? "`detailImages` 和 `detailVideoUrl` 作为详情媒体保存。" : "`detailImages` and `detailVideoUrl` are stored as product detail media."}</li>
                    </ul>
                  </div>
                </div>

                <div
                  className={`controlhub-ops-editor-upload-card controlhub-ops-editor-dropzone${dragTarget === "main" ? " is-dragging" : ""}`}
                  onDragEnter={() => handleUploadDragEnter("main")}
                  onDragOver={(event) => {
                    event.preventDefault();
                    handleUploadDragEnter("main");
                  }}
                  onDragLeave={(event) => handleUploadDragLeave(event, "main")}
                  onDrop={(event) => void handleUploadDrop(event, "mainImagesText", 6, "main")}
                >
                  <div className="controlhub-ops-editor-upload-head">
                    <div>
                      <h6>{isZh ? "主图素材" : "Main Images"}</h6>
                      <p>{isZh ? "与当前接口保持一致，提交前需要 6 张主图。" : "The current endpoint requires 6 main image URLs before submit."}</p>
                    </div>
                    <label className="controlhub-button ghost controlhub-ops-editor-upload-button">
                      {isZh ? "上传主图" : "Upload Main Images"}
                      <input type="file" accept="image/*" multiple onChange={(event) => handleImageUpload("mainImagesText", event, 6)} />
                    </label>
                  </div>
                  <textarea
                    rows={6}
                    placeholder={texts.mainImages}
                    value={newProduct.mainImagesText}
                    onChange={(event) => updateField("mainImagesText", event.target.value)}
                    required
                  />
                  {!mainImages.length ? (
                    <div className="controlhub-ops-editor-empty-state" aria-hidden="true">
                      <div className="controlhub-ops-editor-empty-art is-main">
                        <span className="tile tile-a" />
                        <span className="tile tile-b" />
                        <span className="tile tile-c" />
                      </div>
                      <strong>{isZh ? "拖拽主图到这里" : "Drop main images here"}</strong>
                      <p>{isZh ? "支持直接拖入 6 张图片，首张会自动作为封面图。" : "Drag up to 6 images here. The first one becomes the cover image automatically."}</p>
                    </div>
                  ) : (
                    <div className="controlhub-ops-editor-preview-grid is-main">
                      {mainImages.slice(0, 6).map((imageUrl, index) => (
                        <figure key={`${imageUrl}-${index}`} className={`controlhub-ops-editor-preview-tile${index === 0 ? " is-primary" : ""}`}>
                          <img src={imageUrl} alt={`${newProduct.title || "product"}-${index + 1}`} loading="lazy" />
                          <figcaption>
                            <span>{isZh ? `主图 ${index + 1}` : `Main ${index + 1}`}</span>
                            {index === 0 ? <strong>{isZh ? "封面图" : "Cover"}</strong> : null}
                          </figcaption>
                        </figure>
                      ))}
                    </div>
                  )}
                </div>

                <div
                  className={`controlhub-ops-editor-upload-card controlhub-ops-editor-dropzone${dragTarget === "detail" ? " is-dragging" : ""}`}
                  onDragEnter={() => handleUploadDragEnter("detail")}
                  onDragOver={(event) => {
                    event.preventDefault();
                    handleUploadDragEnter("detail");
                  }}
                  onDragLeave={(event) => handleUploadDragLeave(event, "detail")}
                  onDrop={(event) => void handleUploadDrop(event, "detailImagesText", 12, "detail")}
                >
                  <div className="controlhub-ops-editor-upload-head">
                    <div>
                      <h6>{isZh ? "详情图" : "Detail Images"}</h6>
                      <p>{isZh ? "用于详情页内容区，不再混入物流、服务等说明性字段。" : "Used only for the product detail section without logistics/service filler blocks."}</p>
                    </div>
                    <label className="controlhub-button ghost controlhub-ops-editor-upload-button">
                      {isZh ? "上传详情图" : "Upload Detail Images"}
                      <input type="file" accept="image/*" multiple onChange={(event) => handleImageUpload("detailImagesText", event, 12)} />
                    </label>
                  </div>
                  <textarea
                    rows={6}
                    placeholder={texts.detailImages}
                    value={newProduct.detailImagesText}
                    onChange={(event) => updateField("detailImagesText", event.target.value)}
                  />
                  {!detailImages.length ? (
                    <div className="controlhub-ops-editor-empty-state is-detail" aria-hidden="true">
                      <div className="controlhub-ops-editor-empty-art is-detail">
                        <span className="tile tile-a" />
                        <span className="tile tile-b" />
                        <span className="tile tile-c" />
                      </div>
                      <strong>{isZh ? "拖拽详情图到这里" : "Drop detail images here"}</strong>
                      <p>{isZh ? "适合放场景、结构、材质和包装细节图。" : "Use this area for scene, structure, material, and packaging details."}</p>
                    </div>
                  ) : (
                    <div className="controlhub-ops-editor-preview-grid is-detail">
                      {detailImages.slice(0, 8).map((imageUrl, index) => (
                        <figure key={`${imageUrl}-${index}`} className="controlhub-ops-editor-preview-tile">
                          <img src={imageUrl} alt={`${newProduct.title || "detail"}-${index + 1}`} loading="lazy" />
                          <figcaption>
                            <span>{isZh ? `详情 ${index + 1}` : `Detail ${index + 1}`}</span>
                          </figcaption>
                        </figure>
                      ))}
                    </div>
                  )}
                </div>
              </section>

              <section id="ops-editor-content" className="controlhub-ops-editor-section">
                <div className="controlhub-ops-editor-section-head">
                  <div className="controlhub-ops-editor-section-lead">
                    <span className="controlhub-ops-editor-section-index">04</span>
                    <div>
                      <h5>{isZh ? "参数与详情" : "Parameters and Detail Body"}</h5>
                      <span>{isZh ? "对应参数数组与详情正文，不再包含阶梯价、服务承诺等扩展工作台信息。" : "This section maps to stored parameters and detail content only."}</span>
                    </div>
                  </div>
                  <span className="controlhub-ops-editor-section-tag">{isZh ? "内容描述" : "Content"}</span>
                </div>
                <div className="controlhub-ops-editor-trade-grid">
                  <article className="controlhub-ops-editor-trade-card">
                    <h6>{texts.parameters}</h6>
                    <p>{isZh ? "每行格式：参数名:参数值。" : "One parameter per line using `Key:Value`."}</p>
                    <textarea
                      rows={8}
                      value={newProduct.parametersText}
                      onChange={(event) => updateField("parametersText", event.target.value)}
                      placeholder={isZh ? "材质:TPE\n尺寸:183 x 61 cm\n厚度:6 mm" : "Material:TPE\nSize:183 x 61 cm\nThickness:6 mm"}
                    />
                    <div className="controlhub-ops-editor-chip-row">
                      <span>{isZh ? `已录入 ${parameterRows.length} 条参数` : `${parameterRows.length} parameters added`}</span>
                    </div>
                  </article>

                  <article className="controlhub-ops-editor-trade-card">
                    <h6>{isZh ? "详情正文" : "Detail Content"}</h6>
                    <p>{isZh ? "对应数据库里的详情内容字段，用模块化短段落填写即可。" : "Maps to the stored detail body. Use short modular blocks instead of oversized editorial helpers."}</p>
                    <textarea
                      rows={8}
                      value={newProduct.detailContentText || ""}
                      onChange={(event) => updateField("detailContentText", event.target.value)}
                      placeholder={isZh ? "【核心卖点】防滑纹理，抓地稳定\n【材质说明】高回弹 TPE\n【规格参数】183 x 61 x 0.6 cm" : "[Selling Point] Anti-slip texture with stable grip\n[Material] High-rebound TPE\n[Specs] 183 x 61 x 0.6 cm"}
                    />
                    <div className="controlhub-ops-editor-chip-row">
                      <span>{isZh ? `${detailNarrativeBlocks.length} 个详情段落` : `${detailNarrativeBlocks.length} detail blocks`}</span>
                    </div>
                  </article>
                </div>

                {detailNarrativeBlocks.length ? (
                  <div className="controlhub-ops-editor-detail-copy-preview">
                    {detailNarrativeBlocks.slice(0, 6).map((block, index) => (
                      <article key={`${block}-${index}`}>
                        <span>{isZh ? `段落 ${index + 1}` : `Block ${index + 1}`}</span>
                        <p>{block}</p>
                      </article>
                    ))}
                  </div>
                ) : null}
              </section>

            </form>
          </div>

          <div className="controlhub-ops-editor-footer">
            <div className="controlhub-ops-editor-footer-status">
              <strong>{isZh ? "编辑状态" : "Editor Status"}</strong>
              <span>
                {uploadNotice || (isZh ? `已完成 ${completedSections} / ${editorSections.length} 个区块，当前只保留真实商品字段录入。` : `${completedSections} / ${editorSections.length} sections complete. The editor now contains only persisted product fields.`)}
              </span>
            </div>
            <div className="controlhub-ops-editor-footer-actions row-actions">
              {onBack ? (
                <button type="button" className="controlhub-button ghost" onClick={() => { void onBack(); }}>
                  {isZh ? "返回商品列表" : "Back to Products"}
                </button>
              ) : null}
              <button type="submit" form="ops-product-editor-form" className="controlhub-button ghost">
                {isZh ? "发布" : "Publish"}
              </button>
              <button type="submit" form="ops-product-editor-form" className="controlhub-button">
                {primaryActionLabel || (isZh ? "保存" : texts.create)}
              </button>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

export function NotificationManagementPanel({
  locale,
  viewMode = "manage",
  canManageNotifications = false,
  notificationDraft,
  onChangeNotificationDraft,
  notificationLoading = false,
  notificationSaving = false,
  notificationNotice = "",
  editingNotificationId = 0,
  managedNotifications = [],
  teamOptions = [],
  onStartEditNotification,
  onCancelEditNotification,
  onSubmitNotification,
  onDeleteNotification,
  onRefreshNotifications
}) {
  const isZh = locale !== "en";
  const roleOptions = useMemo(() => ([
    { value: "developer", label: isZh ? "开发者" : "Developer" },
    { value: "super-admin", label: isZh ? "超级管理员" : "Super Admin" },
    { value: "admin", label: isZh ? "管理员" : "Admin" }
  ]), [isZh]);
  const teamOptionMap = useMemo(() => {
    const next = new Map();
    (Array.isArray(teamOptions) ? teamOptions : []).forEach((item) => {
      const userId = Number(item?.id || 0);
      if (Number.isFinite(userId) && userId > 0) {
        next.set(userId, item);
      }
    });
    return next;
  }, [teamOptions]);
  const draftRoleSet = useMemo(
    () => new Set(Array.isArray(notificationDraft?.targetRoles) ? notificationDraft.targetRoles : []),
    [notificationDraft?.targetRoles]
  );
  const draftUserIds = useMemo(() => {
    return String(notificationDraft?.targetUserIdsText || "")
      .split(/[,，\s]+/)
      .map((item) => Number(item || 0))
      .filter((value) => Number.isFinite(value) && value > 0);
  }, [notificationDraft?.targetUserIdsText]);
  const planMetrics = useMemo(() => {
    const source = Array.isArray(managedNotifications) ? managedNotifications : [];
    return {
      total: source.length,
      allStaff: source.filter((item) => item.targetType === "all_staff").length,
      roleScoped: source.filter((item) => item.targetType === "roles").length,
      personScoped: source.filter((item) => item.targetType === "users").length,
      waitingRead: source.reduce((sum, item) => sum + Number(item?.unreadRecipientCount || 0), 0)
    };
  }, [managedNotifications]);
  const planRows = useMemo(() => {
    return (Array.isArray(managedNotifications) ? managedNotifications : []).slice(0, 8).map((item, index) => {
      const createdAt = Number(item?.createdAt || 0) * 1000;
      const createdDate = Number.isFinite(createdAt) && createdAt > 0 ? new Date(createdAt) : null;
      const scheduleLabel = !createdDate
        ? (isZh ? "待排期" : "Planning")
        : createdDate.toISOString().slice(0, 10);
      const targetLabel = item?.targetType === "users"
        ? (isZh ? `指定成员 ${Number(item?.recipientCount || 0)} 人` : `${Number(item?.recipientCount || 0)} direct recipients`)
        : item?.targetType === "roles"
          ? (Array.isArray(item?.targetRoles) && item.targetRoles.length
            ? item.targetRoles.join(" / ")
            : (isZh ? "岗位范围" : "Role scope"))
          : (isZh ? "全体管理员" : "All staff");

      return {
        key: item?.notificationKey || item?.id || `notification-plan-${index + 1}`,
        title: item?.title || (isZh ? "未命名通知" : "Untitled notification"),
        level: String(item?.level || "info"),
        scheduleLabel,
        targetLabel,
        unreadRecipientCount: Number(item?.unreadRecipientCount || 0)
      };
    });
  }, [isZh, managedNotifications]);

  function formatLevel(level) {
    const normalized = String(level || "info").trim().toLowerCase();
    if (normalized === "success") {
      return isZh ? "正常" : "Success";
    }
    if (normalized === "warning") {
      return isZh ? "提醒" : "Warning";
    }
    if (normalized === "danger") {
      return isZh ? "紧急" : "Critical";
    }
    return isZh ? "信息" : "Info";
  }

  function toggleDraftRole(roleCode) {
    const current = Array.isArray(notificationDraft?.targetRoles) ? notificationDraft.targetRoles : [];
    const exists = current.includes(roleCode);
    const nextRoles = exists ? current.filter((item) => item !== roleCode) : [...current, roleCode];
    onChangeNotificationDraft?.("targetRoles", nextRoles);
  }

  return (
    <section className="controlhub-card controlhub-full-width controlhub-notification-admin-panel">
      <div className="controlhub-notification-admin-toolbar">
        <div>
          <p className="controlhub-notification-admin-eyebrow">
            {viewMode === "plan"
              ? (isZh ? "通知计划" : "Notification Plan")
              : (isZh ? "通知管理" : "Notification Management")}
          </p>
          <h4>
            {viewMode === "plan"
              ? (isZh ? "发布节奏与覆盖计划" : "Cadence and Coverage Plan")
              : (isZh ? "发布、修改和删除通知" : "Publish, edit, and remove notifications")}
          </h4>
          <p className="controlhub-notification-admin-caption">
            {viewMode === "plan"
              ? (isZh ? "把当前通知池整理成发布节奏、覆盖范围和未读压力视图。" : "Turn the current notification pool into a cadence, coverage, and unread-pressure view.")
              : (isZh ? "用统一面板维护通知内容、接收范围和后续回查。" : "Maintain content, audience scope, and follow-up review from one panel.")}
          </p>
        </div>

        <div className="row-actions">
          {viewMode === "manage" && editingNotificationId ? (
            <button type="button" className="controlhub-button ghost" onClick={onCancelEditNotification}>
              {isZh ? "取消编辑" : "Cancel Edit"}
            </button>
          ) : null}
          <button type="button" className="controlhub-button ghost" onClick={onRefreshNotifications} disabled={notificationLoading || notificationSaving}>
            {notificationLoading ? (isZh ? "刷新中..." : "Refreshing...") : (isZh ? "刷新列表" : "Refresh")}
          </button>
          {viewMode === "manage" ? (
            <button type="button" className="controlhub-button" onClick={onSubmitNotification} disabled={!canManageNotifications || notificationSaving}>
              {notificationSaving
                ? (isZh ? "提交中..." : "Submitting...")
                : editingNotificationId
                  ? (isZh ? "更新通知" : "Update Notification")
                  : (isZh ? "发布通知" : "Publish Notification")}
            </button>
          ) : null}
        </div>
      </div>

      {notificationNotice ? <p className="state">{notificationNotice}</p> : null}

      {viewMode === "manage" ? (
        <div className="controlhub-notification-admin-layout">
          <div className="controlhub-notification-admin-form-panel">
            <div className="controlhub-notification-admin-grid two-col">
              <label>
                <span>{isZh ? "通知标题" : "Title"}</span>
                <input
                  value={notificationDraft?.title || ""}
                  onChange={(event) => onChangeNotificationDraft?.("title", event.target.value)}
                  placeholder={isZh ? "例如：促销排期更新" : "For example: Campaign schedule update"}
                  disabled={!canManageNotifications || notificationSaving}
                />
              </label>
              <label>
                <span>{isZh ? "优先级" : "Priority"}</span>
                <select
                  value={notificationDraft?.level || "info"}
                  onChange={(event) => onChangeNotificationDraft?.("level", event.target.value)}
                  disabled={!canManageNotifications || notificationSaving}
                >
                  <option value="info">{formatLevel("info")}</option>
                  <option value="success">{formatLevel("success")}</option>
                  <option value="warning">{formatLevel("warning")}</option>
                  <option value="danger">{formatLevel("danger")}</option>
                </select>
              </label>
            </div>

            <label>
              <span>{isZh ? "通知内容" : "Message"}</span>
              <textarea
                rows={6}
                value={notificationDraft?.message || ""}
                onChange={(event) => onChangeNotificationDraft?.("message", event.target.value)}
                placeholder={isZh ? "写清楚执行动作、截止时间和接收对象。" : "Describe the action, timing, and recipients clearly."}
                disabled={!canManageNotifications || notificationSaving}
              />
            </label>

            <div className="controlhub-notification-admin-grid three-col compact">
              {[
                { value: "all_staff", label: isZh ? "全体管理员" : "All staff" },
                { value: "roles", label: isZh ? "按岗位/角色" : "By role" },
                { value: "users", label: isZh ? "指定个人" : "Specific users" }
              ].map((item) => {
                const isActive = (notificationDraft?.targetType || "all_staff") === item.value;
                return (
                  <button
                    key={item.value}
                    type="button"
                    className={`controlhub-notification-admin-segment${isActive ? " is-active" : ""}`}
                    onClick={() => onChangeNotificationDraft?.("targetType", item.value)}
                    disabled={!canManageNotifications || notificationSaving}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>

            {notificationDraft?.targetType === "roles" ? (
              <div className="controlhub-notification-admin-target-box">
                <strong>{isZh ? "接收角色" : "Target roles"}</strong>
                <div className="controlhub-notification-admin-check-grid">
                  {roleOptions.map((item) => (
                    <label key={item.value} className="controlhub-inline-check">
                      <input
                        type="checkbox"
                        checked={draftRoleSet.has(item.value)}
                        onChange={() => toggleDraftRole(item.value)}
                        disabled={!canManageNotifications || notificationSaving}
                      />
                      <span>{item.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ) : null}

            {notificationDraft?.targetType === "users" ? (
              <div className="controlhub-notification-admin-target-box">
                <label>
                  <span>{isZh ? "接收人 ID" : "Recipient IDs"}</span>
                  <textarea
                    rows={3}
                    value={notificationDraft?.targetUserIdsText || ""}
                    onChange={(event) => onChangeNotificationDraft?.("targetUserIdsText", event.target.value)}
                    placeholder={isZh ? "多个 ID 用逗号或空格分隔，例如：12, 18, 25" : "Separate multiple IDs with commas or spaces, for example: 12, 18, 25"}
                    disabled={!canManageNotifications || notificationSaving}
                  />
                </label>
                {draftUserIds.length ? (
                  <div className="controlhub-notification-admin-chip-row">
                    {draftUserIds.slice(0, 12).map((userId) => {
                      const matched = teamOptionMap.get(userId);
                      return (
                        <span key={`draft-user-${userId}`} className="controlhub-notification-admin-chip">
                          #{userId} {matched ? `· ${matched.name || matched.username || matched.email || ""}` : ""}
                        </span>
                      );
                    })}
                  </div>
                ) : null}
                {teamOptions.length ? (
                  <div className="controlhub-notification-admin-suggestion-list">
                    {(Array.isArray(teamOptions) ? teamOptions : []).slice(0, 8).map((item) => (
                      <button
                        key={`notification-user-suggest-${item.id}`}
                        type="button"
                        className="controlhub-button ghost"
                        onClick={() => {
                          const next = Array.from(new Set([...draftUserIds, Number(item.id || 0)].filter((value) => Number.isFinite(value) && value > 0)));
                          onChangeNotificationDraft?.("targetUserIdsText", next.join(", "));
                        }}
                        disabled={!canManageNotifications || notificationSaving}
                      >
                        #{item.id} · {item.name || item.username || item.email || "-"}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="controlhub-notification-admin-list-panel">
            <div className="controlhub-notification-admin-list-head">
              <h5>{isZh ? "通知池" : "Notification Pool"}</h5>
              <span>{isZh ? `${managedNotifications.length} 条通知` : `${managedNotifications.length} notifications`}</span>
            </div>

            <div className="controlhub-notification-admin-list">
              {managedNotifications.map((item) => (
                <article key={item.id} className="controlhub-notification-admin-item">
                  <div className="controlhub-notification-admin-item-head">
                    <div>
                      <div className="small muted">{formatLevel(item.level)} · {item.targetType === "users" ? (isZh ? "指定个人" : "Direct users") : item.targetType === "roles" ? (isZh ? "角色范围" : "Role scope") : (isZh ? "全体管理员" : "All staff")}</div>
                      <strong>{item.title}</strong>
                    </div>
                    <div className="row-actions">
                      <button type="button" className="controlhub-button ghost" onClick={() => onStartEditNotification?.(item)} disabled={!canManageNotifications || notificationSaving}>
                        {isZh ? "修改" : "Edit"}
                      </button>
                      <button type="button" className="controlhub-button ghost admin-commerce-product-action-danger" onClick={() => onDeleteNotification?.(item)} disabled={!canManageNotifications || notificationSaving}>
                        {isZh ? "删除" : "Delete"}
                      </button>
                    </div>
                  </div>
                  <p>{item.message}</p>
                  <div className="controlhub-notification-admin-meta-row">
                    <span>{isZh ? `接收人 ${Number(item.recipientCount || 0)} 人` : `${Number(item.recipientCount || 0)} recipients`}</span>
                    <span>{isZh ? `未读 ${Number(item.unreadRecipientCount || 0)} 人` : `${Number(item.unreadRecipientCount || 0)} unread`}</span>
                    {item.targetType === "roles" && Array.isArray(item.targetRoles) && item.targetRoles.length ? <span>{item.targetRoles.join(" / ")}</span> : null}
                    {item.targetType === "users" && Array.isArray(item.recipientUserIds) && item.recipientUserIds.length ? <span>#{item.recipientUserIds.join(", #")}</span> : null}
                  </div>
                </article>
              ))}
              {!managedNotifications.length ? (
                <div className="controlhub-notification-admin-empty">
                  <p>{isZh ? "还没有已发布通知。左侧表单可直接创建第一条通知。" : "No published notifications yet. Use the form on the left to publish the first one."}</p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <div className="controlhub-notification-plan-layout">
          <div className="controlhub-notification-admin-grid four-col metrics">
            <article className="controlhub-notification-plan-metric">
              <span>{isZh ? "总通知数" : "Total Notifications"}</span>
              <strong>{planMetrics.total}</strong>
            </article>
            <article className="controlhub-notification-plan-metric">
              <span>{isZh ? "全员通知" : "All Staff"}</span>
              <strong>{planMetrics.allStaff}</strong>
            </article>
            <article className="controlhub-notification-plan-metric">
              <span>{isZh ? "角色通知" : "Role Scoped"}</span>
              <strong>{planMetrics.roleScoped}</strong>
            </article>
            <article className="controlhub-notification-plan-metric">
              <span>{isZh ? "未读压力" : "Unread Pressure"}</span>
              <strong>{planMetrics.waitingRead}</strong>
            </article>
          </div>

          <div className="controlhub-notification-admin-layout is-plan">
            <div className="controlhub-notification-admin-list-panel">
              <div className="controlhub-notification-admin-list-head">
                <h5>{isZh ? "计划时间线" : "Planning Timeline"}</h5>
                <span>{isZh ? "按当前通知池自动生成" : "Generated from the current notification pool"}</span>
              </div>
              <div className="controlhub-notification-admin-list">
                {planRows.map((item) => (
                  <article key={item.key} className="controlhub-notification-admin-item is-plan">
                    <div className="controlhub-notification-admin-item-head">
                      <div>
                        <div className="small muted">{item.scheduleLabel} · {formatLevel(item.level)}</div>
                        <strong>{item.title}</strong>
                      </div>
                      <span className="controlhub-notification-admin-chip">{item.targetLabel}</span>
                    </div>
                    <div className="controlhub-notification-admin-meta-row">
                      <span>{isZh ? `未读压力 ${item.unreadRecipientCount}` : `${item.unreadRecipientCount} unread pending`}</span>
                    </div>
                  </article>
                ))}
                {!planRows.length ? (
                  <div className="controlhub-notification-admin-empty">
                    <p>{isZh ? "当前通知池为空，计划视图会在发布通知后自动形成。" : "The plan view populates automatically after notifications are published."}</p>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="controlhub-notification-admin-form-panel is-plan">
              <div className="controlhub-notification-admin-target-box">
                <strong>{isZh ? "计划建议" : "Planning Suggestions"}</strong>
                <div className="controlhub-notification-plan-stack">
                  <article>
                    <h6>{isZh ? "运营节奏" : "Operating Cadence"}</h6>
                    <p>{isZh ? "把紧急类通知控制在少量高优先级项，避免用 danger 覆盖日常更新。" : "Keep danger-level messages scarce and reserve them for genuinely urgent operational changes."}</p>
                  </article>
                  <article>
                    <h6>{isZh ? "接收范围" : "Audience Scope"}</h6>
                    <p>{isZh ? "优先使用角色范围通知；只有明确负责人时再改成指定个人。" : "Prefer role-scoped notifications first, and only switch to direct-user targeting when ownership is explicit."}</p>
                  </article>
                  <article>
                    <h6>{isZh ? "回看机制" : "Review Loop"}</h6>
                    <p>{isZh ? "未读人数持续偏高的通知应回收重发，或拆解成更具体的执行通知。" : "When unread counts stay high, recycle the message or split it into narrower execution notifications."}</p>
                  </article>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export function SystemConfigForm({
  texts,
  locale,
  systemConfigDraft,
  setSystemConfigDraft,
  onSubmit,
  savingSystemConfig,
  systemConfigPath,
  accessRole = "customer" // pass from parent, default to customer
}) {
  const emailTemplates = systemConfigDraft.emailTemplates || {};
  const branding = emailTemplates.branding || {};
  const registration = emailTemplates.registration || {};
  const passwordReset = emailTemplates.passwordReset || {};
  const loginNotice = emailTemplates.loginNotice || {};
  const [activeTab, setActiveTab] = useState("database");
  const [activeEmailTab, setActiveEmailTab] = useState("branding");
  const isZh = locale !== "en";
  const isDevOrSuperAdmin = ["developer", "super-admin"].includes(accessRole);
  const googleOauthQuickStartUrl = "https://developers.google.com/identity/oauth2/web/guides/get-google-api-clientid";
  const googleOauthClientCreateUrl = "https://console.cloud.google.com/apis/credentials";
  const minimumLoginRoleOptions = [
    { value: "guest", zh: "不限制：路人及以上", en: "No restriction: Guest and above" },
    { value: "customer", zh: "客户及以上", en: "Customer and above" },
    { value: "admin", zh: "普通管理员及以上", en: "Admin and above" },
    { value: "super-admin", zh: "超级管理员及以上", en: "Super admin and above" },
    { value: "developer", zh: "开发人员", en: "Developer only" }
  ];

  function isSameLabel(left, right) {
    return String(left || "").trim().toLowerCase() === String(right || "").trim().toLowerCase();
  }

  function renderBilingualText(zh, en, className = "controlhub-bilingual-text") {
    const primary = isZh ? zh : en;
    const secondary = isZh ? en : zh;
    const showSecondary = !isSameLabel(primary, secondary);

    return (
      <span className={className}>
        <strong>{primary}</strong>
        {showSecondary ? <small>{secondary}</small> : null}
      </span>
    );
  }

  function renderBilingualParagraph(zh, en) {
    const primary = isZh ? zh : en;
    const secondary = isZh ? en : zh;

    if (isSameLabel(primary, secondary)) {
      return <p>{primary}</p>;
    }

    return (
      <p>
        {primary}
        <br />
        {secondary}
      </p>
    );
  }

  function updateEmailTemplates(section, field, value) {
    setSystemConfigDraft((prev) => ({
      ...prev,
      emailTemplates: {
        ...(prev.emailTemplates || {}),
        [section]: {
          ...((prev.emailTemplates && prev.emailTemplates[section]) || {}),
          [field]: value
        }
      }
    }));
  }

  function renderTemplateEditor(title, description, sectionKey, templateValue, options = {}) {
    return (
      <section className="controlhub-email-template-section">
        <div className="controlhub-email-template-head">
          <div>
            <h5>{renderBilingualText(title.zh, title.en)}</h5>
            {renderBilingualParagraph(description.zh, description.en)}
          </div>
          {options.includeEnabled ? (
            <label className="controlhub-inline-check">
              <input
                type="checkbox"
                checked={Boolean(templateValue.enabled)}
                onChange={(event) => updateEmailTemplates(sectionKey, "enabled", event.target.checked)}
              />
              {renderBilingualText("启用发送", "Enable sending", "controlhub-bilingual-inline")}
            </label>
          ) : null}
        </div>
        <label>
          {renderBilingualText("主题", "Subject")}
          <input
            value={templateValue.subject || ""}
            onChange={(event) => updateEmailTemplates(sectionKey, "subject", event.target.value)}
          />
        </label>
        <label>
          {renderBilingualText("纯文本模板", "Plain Text Template")}
          <textarea
            className="controlhub-template-code"
            value={templateValue.text || ""}
            onChange={(event) => updateEmailTemplates(sectionKey, "text", event.target.value)}
          />
        </label>
        <label>
          {renderBilingualText("HTML 模板", "HTML Template")}
          <textarea
            className="controlhub-template-code controlhub-template-html"
            value={templateValue.html || ""}
            onChange={(event) => updateEmailTemplates(sectionKey, "html", event.target.value)}
          />
        </label>
      </section>
    );
  }

  const tabs = [
    {
      key: "database",
      label: { zh: "数据库", en: "Database" },
      eyebrow: { zh: "连接与连接池配置", en: "Connection and pool settings" }
    },
    {
      key: "email",
      label: { zh: "邮箱", en: "Email" },
      eyebrow: { zh: "品牌信息与事务邮件模板", en: "Branding and transactional templates" }
    }
  ];

  const emailTabs = [
    { key: "branding", label: { zh: "品牌与发件参数", en: "Branding & Sender" } },
    { key: "registration", label: { zh: "注册邮件", en: "Registration" } },
    { key: "passwordReset", label: { zh: "找回密码邮件", en: "Password Reset" } },
    { key: "loginNotice", label: { zh: "登录通知邮件", en: "Login Notice" } }
  ];

  const activeTabMeta = tabs.find((item) => item.key === activeTab) || tabs[0];

  return (
    <article className="controlhub-card controlhub-full-width controlhub-settings-shell">
      <div className="controlhub-settings-toolbar">
        <div className="controlhub-settings-toolbar-copy">
          <h4>{renderBilingualText("系统设置中心", "System Settings Center")}</h4>
          {renderBilingualParagraph(activeTabMeta.eyebrow.zh, activeTabMeta.eyebrow.en)}
        </div>
        <div className="controlhub-settings-toolbar-badge">{isZh ? "系统设置 / Settings" : "Settings / 系统设置"}</div>
      </div>
      <div className="controlhub-settings-tabs compact" role="tablist" aria-label="System settings categories">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`controlhub-settings-tab ${activeTab === tab.key ? "active" : ""}`}
            role="tab"
            aria-selected={activeTab === tab.key}
            onClick={() => setActiveTab(tab.key)}
          >
            {renderBilingualText(tab.label.zh, tab.label.en)}
            <small>{isZh ? tab.eyebrow.zh : tab.eyebrow.en}</small>
          </button>
        ))}
      </div>
      <form className="controlhub-form controlhub-product-detail-form" onSubmit={onSubmit}>
        <div className="controlhub-settings-panel">
          {activeTab === "database" ? (
            <div className="controlhub-settings-stack">
              <section className="controlhub-email-template-section compact">
                <div className="controlhub-email-template-head">
                  <div>
                    <h5>{renderBilingualText("数据库", texts.settingsTabDatabase || "Database")}</h5>
                    <p>{isZh ? texts.restartRequiredHint : "Changes may require a backend restart to fully apply."}<br />{isZh ? "Changes may require a backend restart to fully apply." : texts.restartRequiredHint}</p>
                  </div>
                </div>
                <div className="controlhub-form-grid two-col">
                  <div style={{ gridColumn: "1/-1" }}>
                    {renderBilingualText(
                      "数据库配置由运维管理，管理后台不允许修改数据库或缓存连接设置。",
                      "Database configuration is managed outside the admin UI; database and cache connection settings cannot be edited here."
                    )}
                  </div>
                </div>
                <div className="controlhub-form-grid two-col">
                  <label>
                    {renderBilingualText("最低允许登录角色", "Minimum login role")}
                    <select
                      value={systemConfigDraft.site?.minimumLoginRole || "guest"}
                      onChange={(event) =>
                        setSystemConfigDraft((prev) => ({
                          ...prev,
                          site: {
                            ...(prev.site || {}),
                            minimumLoginRole: event.target.value
                          }
                        }))
                      }
                    >
                      {minimumLoginRoleOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {isZh ? option.zh : option.en}
                        </option>
                      ))}
                    </select>
                    <small style={{ color: "#6b7280", lineHeight: 1.6 }}>
                      {isZh
                        ? "低于该角色的账号登录会提示当前不开放登录和注册；当门槛高于客户时，新用户注册也会被关闭。"
                        : "Accounts below this role will see the login and registration closed message. Registration closes when the threshold is above customer."}
                    </small>
                  </label>
                </div>
                <div className="controlhub-form-grid two-col">
                  <label className="controlhub-inline-check" style={{ alignItems: "center", gap: "10px" }}>
                    <input
                      type="checkbox"
                      checked={Boolean(systemConfigDraft.site?.websiteEnabled)}
                      onChange={(event) =>
                        setSystemConfigDraft((prev) => ({
                          ...prev,
                          site: {
                            ...(prev.site || {}),
                            websiteEnabled: event.target.checked
                          }
                        }))
                      }
                    />
                    {renderBilingualText("网站访问开关：允许前台访问", "Website access: Allow storefront access")}
                  </label>
                  <label className="controlhub-inline-check" style={{ alignItems: "center", gap: "10px" }}>
                    <input
                      type="checkbox"
                      checked={Boolean(systemConfigDraft.site?.allowUserRegistration)}
                      onChange={(event) =>
                        setSystemConfigDraft((prev) => ({
                          ...prev,
                          site: {
                            ...(prev.site || {}),
                            allowUserRegistration: event.target.checked
                          }
                        }))
                      }
                    />
                    {renderBilingualText("用户注册开关：允许新用户注册", "User registration: Allow new sign-ups")}
                  </label>
                </div>
                <div className="controlhub-form-grid two-col">
                  <label className="controlhub-inline-check" style={{ alignItems: "center", gap: "10px" }}>
                    <input
                      type="checkbox"
                      checked={Boolean(systemConfigDraft.testing?.disableFrontendLoginCaptcha)}
                      onChange={(event) =>
                        setSystemConfigDraft((prev) => ({
                          ...prev,
                          testing: {
                            ...(prev.testing || {}),
                            disableFrontendLoginCaptcha: event.target.checked
                          }
                        }))
                      }
                    />
                    {renderBilingualText("测试开关：前台登录免验证码", "Testing: Disable captcha on storefront login")}
                  </label>
                </div>
                <div className="controlhub-form-grid two-col">
                  <label className="controlhub-inline-check" style={{ alignItems: "center", gap: "10px" }}>
                    <input
                      type="checkbox"
                      checked={Boolean(systemConfigDraft.googleAuth?.enabled)}
                      onChange={(event) =>
                        setSystemConfigDraft((prev) => ({
                          ...prev,
                          googleAuth: {
                            ...(prev.googleAuth || {}),
                            enabled: event.target.checked
                          }
                        }))
                      }
                    />
                    {renderBilingualText("Google 登录开关：允许 Google 授权登录", "Google sign-in: Enable Google OAuth login")}
                  </label>
                </div>
                <div className="controlhub-form-grid two-col">
                  <label className="controlhub-inline-check" style={{ alignItems: "center", gap: "10px" }}>
                    <input
                      type="checkbox"
                      checked={Boolean(systemConfigDraft.googleAuth?.allowInsecureOfflineVerify)}
                      onChange={(event) =>
                        setSystemConfigDraft((prev) => ({
                          ...prev,
                          googleAuth: {
                            ...(prev.googleAuth || {}),
                            allowInsecureOfflineVerify: event.target.checked
                          }
                        }))
                      }
                    />
                    {renderBilingualText("调试兜底：外网不可达时允许离线校验 ID Token（不验签，仅调试）", "Debug fallback: Allow offline ID token checks when outbound is blocked (no signature verification, debug only)")}
                  </label>
                  <label>
                    {renderBilingualText("Google Client ID", "Google Client ID")}
                    <input
                      value={systemConfigDraft.googleAuth?.clientId || ""}
                      onChange={(event) =>
                        setSystemConfigDraft((prev) => ({
                          ...prev,
                          googleAuth: {
                            ...(prev.googleAuth || {}),
                            clientId: event.target.value
                          }
                        }))
                      }
                      placeholder="xxxx.apps.googleusercontent.com"
                    />
                    <div className="row-actions" style={{ marginTop: "8px", justifyContent: "flex-start" }}>
                      <a
                        href={googleOauthClientCreateUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="controlhub-button ghost"
                      >
                        {isZh ? "申请 Google Client ID" : "Apply for Google Client ID"}
                      </a>
                      <a
                        href={googleOauthQuickStartUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="controlhub-button ghost"
                      >
                        {isZh ? "官方指引" : "Official Guide"}
                      </a>
                    </div>
                    <small style={{ color: "#6b7280", lineHeight: 1.6 }}>
                      {isZh
                        ? "点击申请入口后可在 Google Cloud 控制台创建 OAuth Client。受 Google 账号授权流程限制，无法在本系统内全自动代办；创建完成后将 Client ID 粘贴回此处保存即可。"
                        : "Use the application entry to create an OAuth client in Google Cloud Console. Due to Google account authorization flow, fully automatic in-app provisioning is not available; paste the created Client ID here and save."}
                    </small>
                  </label>
                  <label>
                    {renderBilingualText("允许域名（可选）", "Allowed hosted domains (optional)")}
                    <input
                      value={systemConfigDraft.googleAuth?.allowedHostedDomains || ""}
                      onChange={(event) =>
                        setSystemConfigDraft((prev) => ({
                          ...prev,
                          googleAuth: {
                            ...(prev.googleAuth || {}),
                            allowedHostedDomains: event.target.value
                          }
                        }))
                      }
                      placeholder="example.com,example.org"
                    />
                  </label>
                  <label>
                    {renderBilingualText("Google 校验代理（可选）", "Google verify proxy URL (optional)")}
                    <input
                      value={systemConfigDraft.googleAuth?.proxyUrl || ""}
                      onChange={(event) =>
                        setSystemConfigDraft((prev) => ({
                          ...prev,
                          googleAuth: {
                            ...(prev.googleAuth || {}),
                            proxyUrl: event.target.value
                          }
                        }))
                      }
                      placeholder="http://127.0.0.1:7890"
                    />
                    <small style={{ color: "#6b7280", lineHeight: 1.6 }}>
                      {isZh
                        ? "当服务器无法直接访问 Google API 时可填写代理地址（例如公司代理），保存后立即用于后端 Google 凭证校验。"
                        : "Use a proxy URL when the server cannot reach Google APIs directly. The backend will use it immediately for Google credential verification after saving."}
                    </small>
                    <small style={{ color: "#b91c1c", lineHeight: 1.6, display: "block", marginTop: "6px" }}>
                      {isZh
                        ? "安全提示：离线校验兜底不会校验 Google 签名，仅用于开发联调。生产环境请务必关闭并保证后端可访问 Google。"
                        : "Security notice: Offline fallback does not verify Google signatures and is for development only. Keep it disabled in production and ensure backend access to Google."}
                    </small>
                  </label>
                </div>
              </section>
            </div>
          ) : null}

          {activeTab === "email" ? (
            <div className="controlhub-settings-stack">
              <section className="controlhub-email-template-section compact controlhub-settings-section-spotlight">
                <div className="controlhub-email-template-head">
                  <div>
                    {renderBilingualParagraph("品牌信息与事务邮件模板", "Branding and transactional templates")}
                  </div>
                </div>
                <div className="controlhub-settings-tabs compact inner" role="tablist" aria-label="Email configuration sections">
                  {emailTabs.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      className={`controlhub-settings-tab ${activeEmailTab === tab.key ? "active" : ""}`}
                      role="tab"
                      aria-selected={activeEmailTab === tab.key}
                      onClick={() => setActiveEmailTab(tab.key)}
                    >
                      {renderBilingualText(tab.label.zh, tab.label.en)}
                    </button>
                  ))}
                </div>
                {activeEmailTab === "branding" ? (
                <>
                <div className="controlhub-form-grid two-col">
                  <label>
                    {renderBilingualText("公司名称", "Company Name")}
                    <input
                      value={branding.companyName || ""}
                      onChange={(event) => updateEmailTemplates("branding", "companyName", event.target.value)}
                    />
                  </label>
                  <label>
                    {renderBilingualText("支持邮箱", "Support Email")}
                    <input
                      value={branding.supportEmail || ""}
                      onChange={(event) => updateEmailTemplates("branding", "supportEmail", event.target.value)}
                    />
                  </label>
                </div>
                <div className="controlhub-form-grid two-col">
                  <label>
                    {renderBilingualText("强调色", "Accent Color")}
                    <input
                      value={branding.accentColor || ""}
                      onChange={(event) => updateEmailTemplates("branding", "accentColor", event.target.value)}
                      placeholder="#7a5446"
                    />
                  </label>
                  <label>
                    {renderBilingualText("背景色", "Background Color")}
                    <input
                      value={branding.backgroundColor || ""}
                      onChange={(event) => updateEmailTemplates("branding", "backgroundColor", event.target.value)}
                      placeholder="#f6efe8"
                    />
                  </label>
                </div>
                <div className="controlhub-form-grid two-col">
                  <label>
                    {renderBilingualText("Logo 地址", "Logo URL")}
                    <input
                      value={branding.logoUrl || ""}
                      onChange={(event) => updateEmailTemplates("branding", "logoUrl", event.target.value)}
                    />
                  </label>
                  <label>
                    {renderBilingualText("登录地址", "Login URL")}
                    <input
                      value={branding.loginUrl || ""}
                      onChange={(event) => updateEmailTemplates("branding", "loginUrl", event.target.value)}
                    />
                  </label>
                </div>
                <div className="controlhub-form-grid two-col">
                  <label>
                    {renderBilingualText("支持热线", "Support Line")}
                    <input
                      value={branding.supportLine || ""}
                      onChange={(event) => updateEmailTemplates("branding", "supportLine", event.target.value)}
                    />
                  </label>
                  <label>
                    {renderBilingualText("页脚说明", "Footer Note")}
                    <input
                      value={branding.footerNote || ""}
                      onChange={(event) => updateEmailTemplates("branding", "footerNote", event.target.value)}
                    />
                  </label>
                </div>
                {/* SMTP 邮件配置 - Only for developer/super-admin */}
                {isDevOrSuperAdmin ? (
                  <section className="controlhub-email-template-section smtp-config-section">
                    <h5 style={{marginTop:24}}>{renderBilingualText("SMTP 邮件配置", "SMTP Email Configuration")}</h5>
                    <div className="controlhub-form-grid two-col">
                      <label>
                        {renderBilingualText("SMTP 主机", "SMTP Host")}
                        <input
                          value={systemConfigDraft.smtp?.host || ""}
                          onChange={e => setSystemConfigDraft(prev => ({
                            ...prev,
                            smtp: { ...prev.smtp, host: e.target.value }
                          }))}
                        />
                      </label>
                      <label>
                        {renderBilingualText("SMTP 端口", "SMTP Port")}
                        <input
                          type="number"
                          value={systemConfigDraft.smtp?.port || ""}
                          onChange={e => setSystemConfigDraft(prev => ({
                            ...prev,
                            smtp: { ...prev.smtp, port: e.target.value }
                          }))}
                        />
                      </label>
                    </div>
                    <div className="controlhub-form-grid two-col">
                      <label>
                        {renderBilingualText("SMTP 用户名", "SMTP User")}
                        <input
                          value={systemConfigDraft.smtp?.user || ""}
                          onChange={e => setSystemConfigDraft(prev => ({
                            ...prev,
                            smtp: { ...prev.smtp, user: e.target.value }
                          }))}
                        />
                      </label>
                      <label>
                        {renderBilingualText("SMTP 密码", "SMTP Password")}
                        <input
                          type="password"
                          value={systemConfigDraft.smtp?.pass || ""}
                          onChange={e => setSystemConfigDraft(prev => ({
                            ...prev,
                            smtp: { ...prev.smtp, pass: e.target.value }
                          }))}
                        />
                      </label>
                    </div>
                    <div className="controlhub-form-grid two-col">
                      <label>
                        {renderBilingualText("发件邮箱", "From Email")}
                        <input
                          value={systemConfigDraft.smtp?.from || ""}
                          onChange={e => setSystemConfigDraft(prev => ({
                            ...prev,
                            smtp: { ...prev.smtp, from: e.target.value }
                          }))}
                        />
                      </label>
                      <label>
                        {renderBilingualText("安全连接 (SSL/TLS)", "Secure Connection (SSL/TLS)")}
                        <select
                          value={systemConfigDraft.smtp?.secure ? "true" : "false"}
                          onChange={e => setSystemConfigDraft(prev => ({
                            ...prev,
                            smtp: { ...prev.smtp, secure: e.target.value === "true" }
                          }))}
                        >
                          <option value="false">{renderBilingualText("否", "No")}</option>
                          <option value="true">{renderBilingualText("是", "Yes")}</option>
                        </select>
                      </label>
                    </div>
                  </section>
                ) : null}
                <div className="controlhub-template-help">
                  <strong>{isZh ? "支持的占位符" : "Supported placeholders"}</strong>
                  <span>{"{{companyName}} {{supportEmail}} {{accentColor}} {{backgroundColor}} {{year}}"}</span>
                  <span>{"{{userName}} {{email}} {{code}} {{expiresMinutes}} {{loginTime}} {{loginIp}} {{loginUrl}}"}</span>
                </div>
                </>
                ) : null}
              </section>
              {activeEmailTab === "registration" ? renderTemplateEditor(
                { zh: "注册邮件", en: "Registration Email" },
                { zh: "发送注册验证码时使用的模板。", en: "Template used when sending the registration verification code." },
                "registration",
                registration
              ) : null}
              {activeEmailTab === "passwordReset" ? renderTemplateEditor(
                { zh: "找回密码邮件", en: "Password Reset Email" },
                { zh: "用户请求重置密码验证码时使用的模板。", en: "Template used when a user requests a password reset code." },
                "passwordReset",
                passwordReset
              ) : null}
              {activeEmailTab === "loginNotice" ? renderTemplateEditor(
                { zh: "登录通知邮件", en: "Login Notice Email" },
                { zh: "用户成功登录后可选发送的通知邮件。", en: "Optional notification email sent after a successful login." },
                "loginNotice",
                loginNotice,
                { includeEnabled: true }
              ) : null}
            </div>
          ) : null}
        </div>
        {systemConfigPath ? (
          <p className="controlhub-settings-path">
            {isZh ? "配置文件路径" : (texts.systemConfigPathLabel || "Configuration file")}: {systemConfigPath}
          </p>
        ) : null}
        <div className="row-actions">
          <button type="submit" className="controlhub-button" disabled={savingSystemConfig}>
            {isZh ? "保存系统设置 / Save System Settings" : `${texts.saveSystemConfig} / 保存系统设置`}
          </button>
        </div>
      </form>
    </article>
  );
}
